import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DcPage from "@/components/landing/dcv2/DcPage";
import { useToast } from "@/app/hooks/use-toast";
import { Mail, MapPin, ArrowRight, CheckCircle2, Copy, AlertCircle, PenLine, Send } from "lucide-react";

/* ─────────────────────── Canvas tokens ───────────────────────
 *
 * The page is now authored in the v2 canvas language: inline style objects
 * reading the eleven custom properties <DcPage> puts on the root, rather than
 * Tailwind utilities reading the old --landing-* set. Three constants that
 * used to live here are gone rather than translated:
 *
 * - ACCENT_TEXT / ERROR_TEXT, which existed to pair a light value with a dark
 *   one by hand. The palette does that now, so the accent is var(--acc2) and
 *   the error is one literal that clears 4.5:1 on both grounds.
 * - The three FOCUS_RING strings. `.dcv2 :focus-visible` already draws a 2px
 *   var(--acc2) ring on every control in the design, so a second ring here
 *   would only be a competing one.
 */

const SANS = "'Manrope', sans-serif";
const MONO = "'Manrope', sans-serif";
const HAIRLINE = "1px solid rgba(var(--ink-rgb),.13)";

/** One literal, not a pair: it clears 4.5:1 on both --paper and #101216. */
const ERROR_COLOR = "#FF6B6B";

/* Vertical padding only. The side gutter is `.site-gutter` on each section, so
   it can narrow on a phone (global.css, T101026002). */
const SECTION: React.CSSProperties = {
  paddingTop: 118,
  paddingBottom: 118,
  position: "relative",
  overflow: "clip",
};

const INNER: React.CSSProperties = {
  maxWidth: 1240,
  margin: "0 auto",
  position: "relative",
};

const H2: React.CSSProperties = {
  margin: 0,
  fontFamily: SANS,
  fontWeight: 600,
  fontSize: "clamp(28px, 3.4vw, 46px)",
  letterSpacing: "-0.02em",
  lineHeight: 1.14,
};

const H3: React.CSSProperties = {
  margin: 0,
  fontFamily: SANS,
  fontWeight: 600,
  fontSize: 18,
  letterSpacing: "-0.01em",
};

const BODY: React.CSSProperties = {
  margin: 0,
  fontSize: 16,
  color: "rgba(var(--ink-rgb),.66)",
};

const CARD_BODY: React.CSSProperties = {
  margin: 0,
  fontSize: 14.5,
  color: "rgba(var(--ink-rgb),.66)",
};

const BTN_PRIMARY: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  background: "var(--btn)",
  color: "var(--btnfg)",
  border: "none",
  padding: "15px 28px",
  borderRadius: 999,
  fontFamily: SANS,
  fontWeight: 600,
  fontSize: 15,
  cursor: "pointer",
  transition: "transform .2s, box-shadow .2s",
};

const BTN_GHOST: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  background: "transparent",
  border: "1px solid rgba(var(--ink-rgb),.3)",
  color: "var(--ink)",
  padding: "15px 28px",
  borderRadius: 999,
  fontFamily: SANS,
  fontWeight: 600,
  fontSize: 15,
  cursor: "pointer",
  transition: "border-color .2s, background .2s",
};

/** Every field label, legend and metadata caption on the page. */
const LABEL: React.CSSProperties = {
  display: "block",
  fontFamily: MONO,
  fontSize: 11,
  letterSpacing: ".14em",
  textTransform: "uppercase",
  color: "rgba(var(--ink-rgb),.62)",
  marginBottom: 8,
};

/** input, textarea and select share one shape. */
const CONTROL: React.CSSProperties = {
  width: "100%",
  background: "var(--paper2)",
  borderRadius: 12,
  padding: "12px 16px",
  fontFamily: SANS,
  fontSize: 15,
  color: "var(--ink)",
  outline: "none",
  transition: "border-color .2s, background .2s",
};

/**
 * Two fields side by side, collapsing to one when the panel is too narrow.
 * `auto-fit` rather than a breakpoint because inline styles carry no media
 * query, and the panel's width is set by the page grid rather than the
 * viewport — so the field pair has to respond to its own container.
 */
const TWO_UP = "repeat(auto-fit, minmax(min(100%, 200px), 1fr))";

/** The border is the only part that varies, so it is applied per control. */
function controlStyle(invalid: boolean, extra?: React.CSSProperties): React.CSSProperties {
  return {
    ...CONTROL,
    border: `1px solid ${invalid ? ERROR_COLOR : "rgba(var(--ink-rgb),.16)"}`,
    ...extra,
  };
}

/** A decorative cyan bloom, parallaxed by the shared runtime. */
/**
 * The eyebrow slot. The canvas pairs its mark with a label; this page has no
 * eyebrow copy to pair it with, and inventing one would be new copy, so the
 * mark stands alone as the rhythm mark above each heading.
 */
function Mark() {
  return (
    <div
      aria-hidden="true"
      style={{
        fontFamily: MONO,
        fontSize: 11.5,
        letterSpacing: ".16em",
        color: "var(--link)",
        marginBottom: 18,
      }}
    >
      <span className="dc-mark" aria-hidden="true" />
    </div>
  );
}

const INQUIRY_RECIPIENT = "info@ai2aim.ai";

/**
 * Why the visitor came, and what the inbox will see because of it.
 *
 * Previously every message arrived subject-lined "Partnership Inquiry" no
 * matter what it was about, so a demo request and a support problem were
 * indistinguishable to whoever opened the inbox. The choice made here is the
 * one piece of information worth the most at the moment it is captured, so it
 * leads the form and drives the subject line.
 */
const ENQUIRY_TYPES = [
  { id: "demo", label: "Book a demo", subject: "Demo request" },
  { id: "pricing", label: "Pricing", subject: "Pricing enquiry" },
  { id: "partnership", label: "Partnership", subject: "Partnership enquiry" },
  { id: "support", label: "Support", subject: "Support request" },
  { id: "other", label: "Something else", subject: "General enquiry" },
] as const;

type EnquiryTypeId = (typeof ENQUIRY_TYPES)[number]["id"];

/**
 * Every CTA on the site lands on this page, so the type can be preselected
 * from the URL. Aliases are generous on purpose: the links are owned by other
 * pages, and a near-miss like ?type=book-a-demo should still land correctly
 * rather than silently falling back to "no choice made".
 */
const TYPE_ALIASES: Record<string, EnquiryTypeId> = {
  demo: "demo",
  "book-demo": "demo",
  "book-a-demo": "demo",
  "request-demo": "demo",
  walkthrough: "demo",
  trial: "demo",
  pricing: "pricing",
  price: "pricing",
  plan: "pricing",
  plans: "pricing",
  quote: "pricing",
  sales: "pricing",
  partnership: "partnership",
  partner: "partnership",
  partners: "partnership",
  support: "support",
  help: "support",
  issue: "support",
  other: "other",
  general: "other",
  question: "other",
};

/** Query keys checked, in order, for a preselected enquiry type. */
const TYPE_PARAM_KEYS = ["type", "intent", "enquiry", "inquiry"];

function readEnquiryTypeFromParams(params: URLSearchParams): EnquiryTypeId | null {
  for (const key of TYPE_PARAM_KEYS) {
    const raw = params.get(key);
    if (!raw) continue;
    const match = TYPE_ALIASES[raw.trim().toLowerCase()];
    if (match) return match;
  }
  return null;
}

/**
 * Dialling codes.
 *
 * The previous version declared nineteen countries and then offered four in
 * the picker, so anyone outside the US, Canada, UK or Australia could not
 * enter their number correctly. The declared list and the offered list are now
 * the same object. Keyed by ISO code because +1 covers both US and Canada and
 * a Radix Select needs distinct values.
 */
const COUNTRY_CODES = [
  { iso: "US", dial: "+1", name: "United States" },
  { iso: "CA", dial: "+1", name: "Canada" },
  { iso: "GB", dial: "+44", name: "United Kingdom" },
  { iso: "AU", dial: "+61", name: "Australia" },
  { iso: "NZ", dial: "+64", name: "New Zealand" },
  { iso: "DE", dial: "+49", name: "Germany" },
  { iso: "FR", dial: "+33", name: "France" },
  { iso: "NL", dial: "+31", name: "Netherlands" },
  { iso: "IT", dial: "+39", name: "Italy" },
  { iso: "ES", dial: "+34", name: "Spain" },
  { iso: "JP", dial: "+81", name: "Japan" },
  { iso: "KR", dial: "+82", name: "South Korea" },
  { iso: "CN", dial: "+86", name: "China" },
  { iso: "IN", dial: "+91", name: "India" },
  { iso: "SG", dial: "+65", name: "Singapore" },
  { iso: "AE", dial: "+971", name: "United Arab Emirates" },
  { iso: "ZA", dial: "+27", name: "South Africa" },
  { iso: "BR", dial: "+55", name: "Brazil" },
  { iso: "MX", dial: "+52", name: "Mexico" },
] as const;

const DEFAULT_COUNTRY = "US";

function countryLabel(iso: string) {
  const entry = COUNTRY_CODES.find((c) => c.iso === iso);
  return entry ? `${entry.dial} (${entry.iso})` : iso;
}

const JOB_ROLES = [
  { id: "executive", label: "Executive" },
  { id: "manager", label: "Manager / Director" },
  { id: "operations", label: "Operations" },
  { id: "finance", label: "Finance" },
  { id: "people", label: "HR / People" },
  { id: "engineer", label: "Engineering" },
  { id: "other", label: "Other" },
] as const;

/**
 * Message cap.
 *
 * The message is URL-encoded into a mailto:, and the practical ceiling across
 * mail clients and browsers is about 2,000 characters for the whole URL.
 * Encoding inflates prose by roughly half — spaces and newlines each become
 * three characters — and the scaffolding around the message (name, email,
 * phone, company, role, subject, recipient) costs a few hundred more. 1,000
 * characters of message keeps the worst realistic case inside the ceiling.
 */
const NOTES_MAX = 1000;
/** Below this many characters remaining, the counter starts warning. */
const NOTES_WARN_AT = 100;
/** Hard ceiling for the assembled mailto: URL, checked before opening it. */
const MAILTO_URL_MAX = 1900;

const CONTACT_FACTS = [
  { icon: Mail, label: "Email", value: INQUIRY_RECIPIENT, href: `mailto:${INQUIRY_RECIPIENT}` },
  { icon: MapPin, label: "Edmonton", value: "9623-66 Avenue NW\nEdmonton, Alberta T6E 0M2" },
];

/**
 * Stated before the visitor types anything, not discovered after they press
 * the button. The form has no endpoint; it drafts a message in their own mail
 * client. That used to be disguised as a submission, which is the one thing
 * this page must never do again.
 */
const HOW_IT_WORKS = [
  { icon: PenLine, title: "You fill this in", body: "Nothing leaves your browser while you type. There is no form endpoint behind this page." },
  { icon: Mail, title: "We draft the email", body: "Pressing the button opens a message, already written and addressed, in whichever mail app this device uses." },
  { icon: Send, title: "You press send", body: "Until you do, the message has not reached us. You can edit every word of it first." },
];

type FieldName = "enquiryType" | "firstName" | "lastName" | "email" | "phone" | "company" | "notes";
type Errors = Partial<Record<FieldName, string>>;

type FormValues = {
  enquiryType: EnquiryTypeId | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  countryCode: string;
  company: string;
  position: string;
  notes: string;
};

/** Shape check only. Deliverability is the mail client's problem, not ours. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
/** Digits, spaces, dashes, dots, parens and a leading plus. */
const PHONE_SHAPE = /^[+\d][\d\s().-]*$/;

/**
 * Every message names the problem and the recovery, which is why these are
 * sentences rather than "Invalid input".
 */
function validate(values: FormValues): Errors {
  const errors: Errors = {};

  if (!values.enquiryType) {
    errors.enquiryType = "Choose what this is about so it reaches the right person.";
  }
  if (!values.firstName.trim()) {
    errors.firstName = "Enter your first name.";
  }
  if (!values.lastName.trim()) {
    errors.lastName = "Enter your last name.";
  }

  const email = values.email.trim();
  if (!email) {
    errors.email = "Enter your work email so we can reply.";
  } else if (!EMAIL_SHAPE.test(email)) {
    errors.email = "Enter an email address in the form name@company.com.";
  }

  const phone = values.phone.trim();
  if (phone) {
    if (!PHONE_SHAPE.test(phone)) {
      errors.phone = "Use digits, spaces, dashes and brackets only.";
    } else if (phone.replace(/\D/g, "").length < 6) {
      errors.phone = "Enter a full phone number, or leave the field empty.";
    }
  }

  if (!values.company.trim()) {
    errors.company = "Enter your company name.";
  }

  if (values.notes.length > NOTES_MAX) {
    errors.notes = `Shorten your message to ${NOTES_MAX} characters or fewer.`;
  }

  return errors;
}

/* Order used by the error summary, so the list reads down the form rather
   than in whatever order the object happens to enumerate. */
const FIELD_ORDER: FieldName[] = ["enquiryType", "firstName", "lastName", "email", "phone", "company", "notes"];

const FIELD_LABELS: Record<FieldName, string> = {
  enquiryType: "What is this about",
  firstName: "First name",
  lastName: "Last name",
  email: "Work email",
  phone: "Phone number",
  company: "Company",
  notes: "Message",
};

/**
 * One field, wired for assistive tech.
 *
 * A render prop rather than a wrapper because the id, the aria-describedby
 * chain and the invalid flag all have to reach the control itself — passing
 * them down is the whole point of the component, and computing them at each
 * call site is how they drift apart.
 */
function FormField({
  id,
  label,
  hint,
  error,
  required,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: (a11y: { id: string; describedBy: string | undefined; invalid: boolean }) => React.ReactNode;
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div>
      <label htmlFor={id} style={LABEL}>
        {label}
        {required ? (
          <span style={{ color: "var(--acc2)" }} aria-hidden="true">
            {" *"}
          </span>
        ) : (
          <span style={{ marginLeft: 6, color: "rgba(var(--ink-rgb),.62)" }}>
            (optional)
          </span>
        )}
      </label>

      {hint && (
        <p
          id={hintId}
          style={{
            margin: "0 0 10px",
            fontSize: 13,
            lineHeight: 1.6,
            color: "rgba(var(--ink-rgb),.62)",
          }}
        >
          {hint}
        </p>
      )}

      {children({ id, describedBy, invalid: Boolean(error) })}

      <FieldError id={errorId} message={error} />
    </div>
  );
}

/**
 * Inline error text.
 *
 * Not a live region: the error summary above the form already announces the
 * whole set on submit, and seven simultaneous alerts would talk over each
 * other. This is the visible half, tied to its control by aria-describedby so
 * it is read when focus lands there.
 */
function FieldError({ id, message }: { id?: string; message?: string }) {
  if (!message) return null;

  return (
    <p
      id={id}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 6,
        margin: "6px 0 0",
        fontSize: 13,
        lineHeight: 1.45,
        color: ERROR_COLOR,
      }}
    >
      <AlertCircle size={14} style={{ marginTop: 2, flex: "none" }} aria-hidden="true" />
      <span>{message}</span>
    </p>
  );
}

/**
 * The enquiry type, as five visible options rather than a dropdown.
 *
 * A select would hide the one answer that decides where the message lands.
 * Native radios inside a fieldset give arrow-key navigation, "3 of 5" in a
 * screen reader, and a working required state for free; the chips are the
 * label elements, styled.
 *
 * The selected chip is the canvas's filled pill — var(--chip) behind
 * var(--chipfg) — rather than a sliding accent handed between chips by a
 * layout animation. The design ships one transition length for a colour
 * change and no shared-element idiom, so the chip changes state in .2s.
 *
 * The radio itself is a transparent overlay the size of the chip rather than
 * an off-screen `sr-only` input: the design's global focus ring is drawn on
 * the control, and a control parked off-screen would have drawn it there too.
 */
function EnquiryTypePicker({
  value,
  onChange,
  error,
}: {
  value: EnquiryTypeId | null;
  onChange: (next: EnquiryTypeId) => void;
  error?: string;
}) {
  const errorId = error ? "enquiryType-error" : undefined;

  return (
    <fieldset
      aria-describedby={errorId}
      aria-required="true"
      aria-invalid={error ? true : undefined}
      style={{ margin: 0, padding: 0, border: "none" }}
    >
      <legend style={{ ...LABEL, marginBottom: 12, padding: 0 }}>
        What is this about?
        <span style={{ color: "var(--acc2)" }} aria-hidden="true">
          {" *"}
        </span>
      </legend>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        {ENQUIRY_TYPES.map((type) => {
          const selected = value === type.id;
          const inputId = `enquiryType-${type.id}`;

          return (
            <span key={type.id} style={{ position: "relative", display: "inline-flex" }}>
              <input
                type="radio"
                id={inputId}
                name="enquiryType"
                value={type.id}
                checked={selected}
                onChange={() => onChange(type.id)}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  margin: 0,
                  opacity: 0,
                  borderRadius: 999,
                  appearance: "none",
                  WebkitAppearance: "none",
                  cursor: "pointer",
                }}
              />
              <label
                htmlFor={inputId}
                className={selected ? undefined : "dc-h-intent"}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: "pointer",
                  borderRadius: 999,
                  padding: "10px 18px",
                  fontFamily: SANS,
                  fontSize: 14,
                  fontWeight: 600,
                  border: selected
                    ? "1px solid var(--chip)"
                    : "1px solid rgba(var(--ink-rgb),.16)",
                  background: selected ? "var(--chip)" : "transparent",
                  color: selected ? "var(--chipfg)" : "rgba(var(--ink-rgb),.66)",
                  transition: "border-color .2s, background .2s, color .2s",
                }}
              >
                {type.label}
              </label>
            </span>
          );
        })}
      </div>

      <FieldError id={errorId} message={error} />
    </fieldset>
  );
}

/**
 * Character counter for the message.
 *
 * The visible number updates on every keystroke; the announced one is debounced
 * so a screen reader is not read a new count per character. Only announced once
 * the visitor is near the cap, which is the only point at which the number is
 * news.
 */
function CharacterCount({ used, max }: { used: number; max: number }) {
  const remaining = max - used;
  const warning = remaining <= NOTES_WARN_AT;
  const [announced, setAnnounced] = useState("");

  useEffect(() => {
    if (!warning) {
      setAnnounced("");
      return;
    }
    const timer = window.setTimeout(() => {
      setAnnounced(
        remaining > 0
          ? `${remaining} characters remaining.`
          : "You have reached the character limit.",
      );
    }, 600);
    return () => window.clearTimeout(timer);
  }, [remaining, warning]);

  return (
    <>
      <span
        aria-hidden="true"
        style={{
          fontFamily: MONO,
          fontSize: 11,
          fontVariantNumeric: "tabular-nums",
          color: "rgba(var(--ink-rgb),.62)",
        }}
      >
        <span style={warning ? { color: ERROR_COLOR, fontWeight: 500 } : undefined}>{used}</span>
        {` / ${max}`}
      </span>
      <span
        role="status"
        aria-live="polite"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          clipPath: "inset(50%)",
          whiteSpace: "nowrap",
        }}
      >
        {announced}
      </span>
    </>
  );
}

/** Why the draft did not open, when it did not. */
type FailureReason = "blocked" | "too-long";

type InquirySummary = {
  subject: string;
  body: string;
  mailtoUrl: string;
  /** Whether the visitor's email client actually opened. */
  mailClientOpened: boolean;
  failureReason?: FailureReason;
};

export default function Contact() {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  /* Every CTA on the site lands here, so an enquiry type arriving in the URL
     is treated as the visitor having already answered the first question. */
  const presetType = useMemo(() => readEnquiryTypeFromParams(searchParams), [searchParams]);

  const [values, setValues] = useState<FormValues>(() => ({
    enquiryType: presetType,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: DEFAULT_COUNTRY,
    company: "",
    position: "",
    notes: "",
  }));

  const [errors, setErrors] = useState<Errors>({});
  /* Errors appear on the first submit, then track every keystroke — so the
     form corrects itself as it is fixed instead of scolding as it is filled. */
  const [attempted, setAttempted] = useState(false);
  const [submitted, setSubmitted] = useState<InquirySummary | null>(null);

  const summaryRef = useRef<HTMLDivElement>(null);

  /* A late-arriving ?type= (a client-side nav from another page's CTA) should
     still land, but must never overwrite a choice the visitor has made. */
  useEffect(() => {
    if (!presetType) return;
    setValues((prev) => (prev.enquiryType ? prev : { ...prev, enquiryType: presetType }));
  }, [presetType]);

  function setField<K extends keyof FormValues>(key: K, next: FormValues[K]) {
    setValues((prev) => {
      const updated = { ...prev, [key]: next };
      if (attempted) setErrors(validate(updated));
      return updated;
    });
  }

  function resetForm() {
    setValues({
      enquiryType: presetType,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      countryCode: DEFAULT_COUNTRY,
      company: "",
      position: "",
      notes: "",
    });
    setErrors({});
    setAttempted(false);
    setSubmitted(null);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setAttempted(true);

    const found = validate(values);
    setErrors(found);

    if (Object.keys(found).length > 0) {
      /* Focus the summary rather than the first bad field: it names how many
         problems there are and links to each one, which is the difference
         between "something is wrong" and knowing what. */
      window.requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }

    const trimmed = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      company: values.company.trim(),
      notes: values.notes.trim(),
    };

    const type = ENQUIRY_TYPES.find((t) => t.id === values.enquiryType);
    const roleLabel = JOB_ROLES.find((r) => r.id === values.position)?.label ?? "Not specified";
    const phoneLine = trimmed.phone ? `${countryLabel(values.countryCode)} ${trimmed.phone}` : "Not provided";

    /* The subject carries the intent. Whoever opens the inbox can now tell a
       demo request from a pricing question without reading the body. */
    const subject = `${type?.subject ?? "Enquiry"} — ${trimmed.firstName} ${trimmed.lastName} (${trimmed.company})`;
    const body = [
      `Enquiry type: ${type?.label ?? "Not specified"}`,
      `Name: ${trimmed.firstName} ${trimmed.lastName}`,
      `Email: ${trimmed.email}`,
      `Phone: ${phoneLine}`,
      `Company: ${trimmed.company}`,
      `Role: ${roleLabel}`,
      "",
      trimmed.notes ? `Message:\n${trimmed.notes}` : "No additional message.",
    ].join("\n");

    const mailtoUrl = `mailto:${INQUIRY_RECIPIENT}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    /* This form does not POST anywhere — it hands the message to the visitor's
       email client. Whether that worked decides what we tell them, so the
       result is captured rather than swallowed. A popup-blocked window (the
       default in Chrome for _blank opened from a submit) returns null. */
    let mailClientOpened = false;
    let failureReason: FailureReason | undefined;

    if (mailtoUrl.length > MAILTO_URL_MAX) {
      /* Long mailto: URLs are truncated silently by some clients and rejected
         outright by others. Refusing to open a draft we know may arrive
         mangled is more honest than opening one and hoping. */
      failureReason = "too-long";
    } else {
      try {
        const win = window.open(mailtoUrl, "_blank", "noopener,noreferrer");
        mailClientOpened = win !== null && win !== undefined;
      } catch {
        mailClientOpened = false;
      }
      if (!mailClientOpened) failureReason = "blocked";
    }

    setSubmitted({ subject, body, mailtoUrl, mailClientOpened, failureReason });
  }

  async function copyInquiryDetails(summary: InquirySummary) {
    const payload = `To: ${INQUIRY_RECIPIENT}\nSubject: ${summary.subject}\n\n${summary.body}`;
    try {
      await navigator.clipboard.writeText(payload);
      toast({ title: "Copied to clipboard", description: "Paste it into your email client to send." });
    } catch {
      toast({
        title: "Couldn't copy automatically",
        description: `Please email ${INQUIRY_RECIPIENT} with your details.`,
        variant: "destructive",
      });
    }
  }

  /* Focus the outcome heading when the panel replaces the form, so a screen
     reader is told what happened instead of being left on a removed button.
     A callback ref rather than an effect on `submitted`: the ref fires the
     moment the heading is attached, which is the only point at which there is
     something to focus. Kept as a ref rather than simplified to an effect now
     that the exit animation is gone, because the ordering guarantee is the
     point — an effect would still be one commit late on a slow mount. */
  const focusResultHeading = useCallback((node: HTMLHeadingElement | null) => {
    node?.focus();
  }, []);

  const errorList = FIELD_ORDER.filter((f) => errors[f]).map((f) => ({ field: f, message: errors[f]! }));
  const showSummary = attempted && errorList.length > 0;

  return (
    <DcPage>
      {/* ── The claim ── */}
      <section className="site-gutter" style={{ ...SECTION, paddingBottom: 56 }}>
        <div style={INNER}>
          <div data-rv="" style={{ maxWidth: 900 }}>
            <Mark />
            <h1
              style={{
                margin: 0,
                fontFamily: SANS,
                fontWeight: 800,
                fontSize: "clamp(38px, 4.6vw, 64px)",
                lineHeight: 1.08,
                letterSpacing: "-0.025em",
              }}
            >
              Tell us how your teams work today.
            </h1>
          </div>
          <p
            data-rv=""
            style={{
              margin: "24px 0 0",
              maxWidth: 680,
              fontSize: 17,
              color: "rgba(var(--ink-rgb),.7)",
            }}
          >
            Describe the work that eats your week, across HR, accounting,
            and everyday operations, and we&rsquo;ll show you what AI2Aim
            would take off your hands.
          </p>
        </div>
      </section>

        {/* ── The working area ── */}
        {/* Not aria-labelledby'd: the heading it would point at is inside the
            form, which is replaced by the outcome panel on submit — the label
            would dangle exactly when it mattered. */}
      <section className="site-gutter" style={{ ...SECTION, paddingTop: 0 }}>
        <div
          style={{
            ...INNER,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 400px), 1fr))",
            gap: 64,
            alignItems: "start",
          }}
        >
          {/* ── Aside: how this actually works, then the facts ── */}
          <div style={{ maxWidth: 460 }}>
            <div data-rv="">
              <h2 style={H2}>This form doesn&rsquo;t send anything.</h2>
              <p style={{ ...BODY, marginTop: 16, lineHeight: 1.7 }}>
                This page writes the email for you and hands it to your own
                mail app. You keep control of it the whole way.
              </p>
            </div>

            <ol
              data-rv=""
              style={{ listStyle: "none", display: "grid", gap: 26, margin: "38px 0 0", padding: 0 }}
            >
              {HOW_IT_WORKS.map((step) => (
                <li key={step.title} style={{ display: "flex", gap: 16 }}>
                  <span
                    aria-hidden="true"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: "none",
                      width: 40,
                      height: 40,
                      borderRadius: 14,
                      background: "var(--chip)",
                      color: "#9DF1FF",
                    }}
                  >
                    <step.icon size={17} />
                  </span>
                  <div>
                    <h3 style={H3}>{step.title}</h3>
                    <p style={{ ...CARD_BODY, marginTop: 7, lineHeight: 1.65 }}>{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>

            <div
              data-rv=""
              style={{
                display: "grid",
                gap: 22,
                marginTop: 40,
                paddingTop: 34,
                borderTop: "1px solid rgba(var(--ink-rgb),.2)",
              }}
            >
              {CONTACT_FACTS.map((fact) => (
                <div key={fact.label} style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <fact.icon
                    size={16}
                    style={{ marginTop: 3, flex: "none", color: "rgba(var(--ink-rgb),.62)" }}
                    aria-hidden="true"
                  />
                  <div>
                    <h3 style={{ ...LABEL, margin: "0 0 4px" }}>{fact.label}</h3>
                    {fact.href ? (
                      <a
                        href={fact.href}
                        className="dc-h-cyan"
                        style={{ fontSize: 15.5, fontWeight: 600, transition: "color .2s" }}
                      >
                        {fact.value}
                      </a>
                    ) : (
                      <p
                        style={{
                          margin: 0,
                          whiteSpace: "pre-line",
                          fontSize: 15.5,
                          fontWeight: 500,
                          color: "var(--ink)",
                        }}
                      >
                        {fact.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── The panel ── */}
          {/* data-rv sits on the panel, never on the two branches inside it:
              the reveal observer is wired once at mount, so anything that
              appears later would be left parked at opacity 0. */}
          {/* Padding on `.contact-panel` (global.css): 36px, and 20px on a
              phone so the form fields keep their width (T101026002). */}
          <div
            data-rv=""
            className="contact-panel"
            style={{
              background: "var(--paper2)",
              border: HAIRLINE,
              borderRadius: 18,
            }}
          >
            {submitted ? (
              <div>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <span
                    aria-hidden="true"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: "none",
                      width: 42,
                      height: 42,
                      borderRadius: 14,
                      background: "rgba(var(--ink-rgb),.04)",
                      border: `1px solid ${submitted.mailClientOpened ? "rgba(var(--ink-rgb),.13)" : ERROR_COLOR}`,
                      color: submitted.mailClientOpened ? "var(--acc2)" : ERROR_COLOR,
                    }}
                  >
                    {submitted.mailClientOpened ? <CheckCircle2 size={19} /> : <AlertCircle size={19} />}
                  </span>
                  <div>
                    {/* Focus lands here so the outcome is announced. */}
                    <h2
                      ref={focusResultHeading}
                      tabIndex={-1}
                      style={{
                        ...H2,
                        fontSize: "clamp(24px, 2.6vw, 34px)",
                        outline: "none",
                      }}
                    >
                      {submitted.mailClientOpened
                        ? "Your message is ready to send"
                        : "We couldn't open your email app"}
                    </h2>
                    <p style={{ ...BODY, marginTop: 12, lineHeight: 1.7 }}>
                      {submitted.mailClientOpened
                        ? "We've drafted your message and opened it in your email app. It has not reached us until you press send there."
                        : submitted.failureReason === "too-long"
                          ? "Your message is too long to hand to an email app reliably. Some clients would cut it off without telling you. Copy the details below and email us directly. Nothing has been sent yet."
                          : "Your browser blocked the new tab, or no email app is set up on this device. Copy the details below and email us directly. Nothing has been sent yet."}
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 26,
                    borderRadius: 12,
                    padding: 18,
                    fontSize: 14.5,
                    background: "rgba(var(--ink-rgb),.04)",
                    border: HAIRLINE,
                    color: "var(--ink)",
                  }}
                >
                  <p style={{ margin: 0 }}>
                    <span style={{ fontWeight: 600 }}>To:</span>{" "}
                    <a href={`mailto:${INQUIRY_RECIPIENT}`} className="dc-h-cyan" style={{ transition: "color .2s" }}>
                      {INQUIRY_RECIPIENT}
                    </a>
                  </p>
                  <p style={{ margin: "6px 0 0" }}>
                    <span style={{ fontWeight: 600 }}>Subject:</span> {submitted.subject}
                  </p>
                  <pre
                    style={{
                      margin: "14px 0 0",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "break-word",
                      fontFamily: SANS,
                      fontSize: 13.5,
                      lineHeight: 1.7,
                      color: "rgba(var(--ink-rgb),.66)",
                    }}
                  >
                    {submitted.body}
                  </pre>
                </div>

                <div style={{ marginTop: 26, display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {/* Only offered when a draft can actually be opened —
                      a retry button on a message too long to survive the
                      trip would be a second lie. */}
                  {submitted.failureReason !== "too-long" && (
                    <a
                      href={submitted.mailtoUrl}
                      className="dc-h-btn-lg"
                      style={{ ...BTN_PRIMARY, flex: "1 1 220px" }}
                    >
                      {submitted.mailClientOpened ? "Open the draft again" : "Try opening it again"}
                      <ArrowRight size={16} aria-hidden="true" />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => copyInquiryDetails(submitted)}
                    className="dc-h-ghost"
                    style={{ ...BTN_GHOST, flex: "1 1 220px" }}
                  >
                    <Copy size={16} aria-hidden="true" />
                    Copy details
                  </button>
                </div>

                <button
                  type="button"
                  onClick={resetForm}
                  className="dc-h-ink"
                  style={{
                    marginTop: 24,
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    fontFamily: MONO,
                    fontSize: 12,
                    letterSpacing: ".12em",
                    textTransform: "uppercase",
                    color: "rgba(var(--ink-rgb),.62)",
                    textDecoration: "underline",
                    textUnderlineOffset: 4,
                    transition: "color .2s",
                  }}
                >
                  Write another message
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate style={{ display: "grid", gap: 26 }}>
                <h2 style={{ ...H2, fontSize: "clamp(24px, 2.6vw, 34px)" }}>
                  Write us a message.
                </h2>

                {/* Named problems, in form order, each one a link to the
                    field it belongs to. Focus lands here on a failed
                    submit; role="alert" announces the count. */}
                {showSummary && (
                  <div
                    ref={summaryRef}
                    tabIndex={-1}
                    role="alert"
                    style={{
                      borderRadius: 12,
                      padding: 18,
                      background: "rgba(255,107,107,.06)",
                      border: `1px solid ${ERROR_COLOR}`,
                    }}
                  >
                    <p
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        margin: 0,
                        fontSize: 14.5,
                        fontWeight: 600,
                        color: ERROR_COLOR,
                      }}
                    >
                      <AlertCircle size={16} style={{ flex: "none" }} aria-hidden="true" />
                      {errorList.length === 1
                        ? "One thing needs fixing before this can be drafted"
                        : `${errorList.length} things need fixing before this can be drafted`}
                    </p>
                    <ul style={{ listStyle: "none", margin: "10px 0 0", padding: "0 0 0 24px", display: "grid", gap: 5 }}>
                      {errorList.map(({ field, message }) => (
                        <li key={field}>
                          <a
                            href={`#${field === "enquiryType" ? "enquiryType-demo" : field}`}
                            style={{
                              fontSize: 13.5,
                              color: ERROR_COLOR,
                              textDecoration: "underline",
                              textUnderlineOffset: 2,
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              const target = document.getElementById(
                                field === "enquiryType" ? "enquiryType-demo" : field,
                              );
                              target?.focus();
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>{FIELD_LABELS[field]}:</span> {message}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <EnquiryTypePicker
                  value={values.enquiryType}
                  onChange={(next) => setField("enquiryType", next)}
                  error={errors.enquiryType}
                />

                <div style={{ display: "grid", gap: 20, gridTemplateColumns: TWO_UP }}>
                  <FormField id="firstName" label="First name" required error={errors.firstName}>
                    {({ id, describedBy, invalid }) => (
                      <input
                        id={id}
                        name="firstName"
                        required
                        autoComplete="given-name"
                        value={values.firstName}
                        onChange={(e) => setField("firstName", e.target.value)}
                        aria-invalid={invalid}
                        aria-describedby={describedBy}
                        style={controlStyle(invalid)}
                      />
                    )}
                  </FormField>

                  <FormField id="lastName" label="Last name" required error={errors.lastName}>
                    {({ id, describedBy, invalid }) => (
                      <input
                        id={id}
                        name="lastName"
                        required
                        autoComplete="family-name"
                        value={values.lastName}
                        onChange={(e) => setField("lastName", e.target.value)}
                        aria-invalid={invalid}
                        aria-describedby={describedBy}
                        style={controlStyle(invalid)}
                      />
                    )}
                  </FormField>
                </div>

                <FormField id="email" label="Work email" required error={errors.email}>
                  {({ id, describedBy, invalid }) => (
                    <input
                      id={id}
                      name="email"
                      type="email"
                      required
                      inputMode="email"
                      autoComplete="email"
                      value={values.email}
                      onChange={(e) => setField("email", e.target.value)}
                      aria-invalid={invalid}
                      aria-describedby={describedBy}
                      style={controlStyle(invalid)}
                    />
                  )}
                </FormField>

                <FormField id="phone" label="Phone number" error={errors.phone}>
                  {({ id, describedBy, invalid }) => (
                    <div style={{ display: "flex", gap: 12 }}>
                      <select
                        name="countryCode"
                        aria-label="Country dialling code"
                        value={values.countryCode}
                        onChange={(e) => setField("countryCode", e.target.value)}
                        style={controlStyle(false, { width: "auto", flex: "none", padding: "12px 10px" })}
                      >
                        {COUNTRY_CODES.map((country) => (
                          <option key={country.iso} value={country.iso}>
                            {`${country.dial} (${country.iso})`}
                          </option>
                        ))}
                      </select>
                      <input
                        id={id}
                        name="phone"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel-national"
                        value={values.phone}
                        onChange={(e) => setField("phone", e.target.value)}
                        aria-invalid={invalid}
                        aria-describedby={describedBy}
                        style={controlStyle(invalid, { flex: "1 1 auto", minWidth: 0 })}
                      />
                    </div>
                  )}
                </FormField>

                <div style={{ display: "grid", gap: 20, gridTemplateColumns: TWO_UP }}>
                  <FormField id="company" label="Company" required error={errors.company}>
                    {({ id, describedBy, invalid }) => (
                      <input
                        id={id}
                        name="company"
                        required
                        autoComplete="organization"
                        value={values.company}
                        onChange={(e) => setField("company", e.target.value)}
                        aria-invalid={invalid}
                        aria-describedby={describedBy}
                        style={controlStyle(invalid)}
                      />
                    )}
                  </FormField>

                  <FormField id="position" label="Your role">
                    {({ id }) => (
                      <select
                        id={id}
                        name="position"
                        value={values.position}
                        onChange={(e) => setField("position", e.target.value)}
                        style={controlStyle(false, {
                          color: values.position ? "var(--ink)" : "rgba(var(--ink-rgb),.45)",
                        })}
                      >
                        {/* The placeholder is the empty value, so the control
                            still reports "no role chosen" rather than
                            defaulting the visitor into the first option. */}
                        <option value="">Select role</option>
                        {JOB_ROLES.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </FormField>
                </div>

                <FormField
                  id="notes"
                  label="Message"
                  error={errors.notes}
                  hint={`What you want to solve, and anything we should know first. Up to ${NOTES_MAX} characters. This becomes the body of the email.`}
                >
                  {({ id, describedBy, invalid }) => (
                    <div>
                      <textarea
                        id={id}
                        name="notes"
                        rows={5}
                        maxLength={NOTES_MAX}
                        value={values.notes}
                        onChange={(e) => setField("notes", e.target.value)}
                        aria-invalid={invalid}
                        aria-describedby={describedBy}
                        placeholder="We run payroll and onboarding across three systems and reconcile by hand every month…"
                        style={controlStyle(invalid, { resize: "vertical", lineHeight: 1.65, display: "block" })}
                      />
                      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                        <CharacterCount used={values.notes.length} max={NOTES_MAX} />
                      </div>
                    </div>
                  )}
                </FormField>

                <div style={{ paddingTop: 24, borderTop: "1px solid rgba(var(--ink-rgb),.2)" }}>
                  {/* The load-bearing sentence, at the point of action. */}
                  <p style={{ ...CARD_BODY, marginBottom: 16, lineHeight: 1.65 }}>
                    This opens a draft in your email app. Nothing reaches
                    AI2Aim until you press send there.
                  </p>
                  <button type="submit" className="dc-h-btn-lg" style={{ ...BTN_PRIMARY, width: "100%" }}>
                    Open in your email app
                    <ArrowRight size={16} aria-hidden="true" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </DcPage>
  );
}
